// app/providers.js
'use client'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState, lazy, Suspense } from 'react'

// Lazy load devtools only in development
const ReactQueryDevtools = lazy(() =>
  process.env.NODE_ENV === 'development'
    ? import('@tanstack/react-query-devtools').then((module) => ({
        default: module.ReactQueryDevtools,
      }))
    : Promise.resolve({ default: () => null })
)

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'),
  )
  if (!match) return undefined
  try {
    return decodeURIComponent(match[1])
  } catch {
    // A malformed percent-escape anywhere on the domain would otherwise
    // throw here during module init and break the entire provider stack.
    return undefined
  }
}

if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_POSTHOG_KEY && !posthog.__loaded) {
  // Bootstrap from cookies set by proxy.ts so the client agrees with the
  // edge on distinct_id and flag values. Prevents any flicker between
  // SSR and hydration when an A/B test rewrites the page.
  const distinctID = readCookie('ph_distinct_id')
  const rawFlags = readCookie('ph_bootstrap')
  let featureFlags: Record<string, string | boolean> | undefined
  if (rawFlags) {
    try {
      featureFlags = JSON.parse(rawFlags)
    } catch {
      featureFlags = undefined
    }
  }

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: "/ingest", // Use reverse proxy to circumvent ad blockers
    ui_host: "https://us.i.posthog.com", // PostHog UI remains on their domain
    person_profiles: 'always',
    capture_pageview: false, // We'll capture manually for better control
    capture_pageleave: true,
    cross_subdomain_cookie: false, // Different domains, not subdomains
    persistence: 'localStorage+cookie',
    bootstrap: {
      distinctID,
      featureFlags,
    },
    loaded: (posthog) => {
      // Align posthog-js's internal distinct_id with the one the edge
      // used for bucketing. Without this, exposure events fired from
      // the edge ID and later conversions fired from the localStorage
      // ID posthog-js already had would attribute to different users,
      // breaking the experiment funnel for returning visitors.
      if (distinctID) posthog.identify(distinctID)

      // Capture affiliate parameter with "first contact wins" logic
      const params = new URLSearchParams(window.location.search);
      const urlAffiliate = params.get('affiliate');
      const storedAffiliate = localStorage.getItem('affiliate');

      // First contact wins: only set if not already stored
      if (urlAffiliate && !storedAffiliate) {
        // New affiliate, store it
        localStorage.setItem('affiliate', urlAffiliate);
        posthog.register({
          affiliate: urlAffiliate,
          initial_affiliate: urlAffiliate,
        });
      } else if (storedAffiliate) {
        // Use existing affiliate (first contact wins)
        posthog.register({
          affiliate: storedAffiliate,
          initial_affiliate: storedAffiliate,
        });
      }
      // If urlAffiliate exists but storedAffiliate also exists, ignore urlAffiliate (first wins)

      // Capture UTM parameters as super properties
      const utmParams: Record<string, string> = {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
        const value = params.get(key);
        if (value) {
          utmParams[key] = value;
        }
      });

      if (Object.keys(utmParams).length > 0) {
        posthog.register(utmParams);
      }
    },
  });
}

export function CSPostHogProvider({ children }: { children: React.ReactNode }) {
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Increased stale time for leaderboard data (5 minutes)
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000, // 10 minutes
            // Retry failed requests twice
            retry: 2,
            // Don't refetch on window focus in production
            refetchOnWindowFocus: process.env.NODE_ENV === 'development',
          },
        },
      })
  )

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === 'development' && (
        <Suspense fallback={null}>
          <ReactQueryDevtools initialIsOpen={false} />
        </Suspense>
      )}
    </QueryClientProvider>
  )
}